import {Injectable} from "@angular/core";
import {from, Observable} from "rxjs";
import {CartLine} from "../domain/dtos/cart-line";
import {CartLineDAO} from "@app/core/cart/infrastructure/cart-line.DAO";
import {CartLineDb} from "@app/core/cart/infrastructure/local/cart-line-db";

@Injectable({
    providedIn: "root",
})
export class CartLineRepository {

    constructor(
        private cartLineDAO: CartLineDAO
    ) { }

    create(cartLine: CartLine): Observable<CartLine> {
        return from(this._createOrUpdate(cartLine));
    }

    addQuantity(cartLine: CartLine) {
        return from(this._createOrAdd(cartLine));
    }

    getQuantity(productCode: string, promotionCode: string): Observable<number> {
        return from(this._getQuantity(productCode, promotionCode));
    }

    isPromotionAdded(promotionCode: string): Observable<boolean> {
        return from(this._isPromotionAdded(promotionCode));
    }

    removeProduct(productCode: string): Observable<CartLine | null> {
        return from(this._removeProduct(productCode));
    }

    removePromotion(promotionCode: string): Observable<CartLine[]> {
        return from(this._removePromotion(promotionCode));
    }

    getCartLines(): Observable<CartLine[]> {
        return from(this.cartLineDAO.findAll());
    }

    clearAllCartLines(): Observable<void> {
        return from(this.cartLineDAO.clear());
    }

    private async _createOrUpdate(cartLine: CartLine): Promise<CartLine> {
        // Check if cartLine exists
        let cartLineDB = await this.cartLineDAO.find(cartLine.code, cartLine.promotionCode);

        // Update quantity if exists, create if not
        if (cartLineDB) {
            const newCartLineDB: CartLineDb = {
                ...cartLine
            }
            return this.cartLineDAO.update(newCartLineDB);
        } else {
            return this.cartLineDAO.save(cartLine);
        }
    }

    private async _createOrAdd(cartLine: CartLine): Promise<CartLine> {
        // Check if cartLine exists
        let cartLineDB = await this.cartLineDAO.find(cartLine.code, cartLine.promotionCode);

        // Increment quantity if exists, create if not
        if (cartLineDB) {
            cartLineDB.quantity += cartLine.quantity;
            return this.cartLineDAO.update(cartLineDB);
        } else {
            return this.cartLineDAO.save(cartLine);
        }
    }

    private async _getQuantity(productCode: string, promotionCode: string): Promise<number> {
        // Check if cartLine exists
        let cartLineDB = await this.cartLineDAO.find(productCode, promotionCode);

        // Return cartLine quantity or 0 if not exists
        if (cartLineDB) {
            return cartLineDB.quantity;
        }

        return 0;
    }

    private async _isPromotionAdded(promotionCode: string): Promise<boolean> {
        // Check how many cartLines have the given promotionCode
        const cartLines = await this.cartLineDAO.findByPromotionCode(promotionCode);
        return cartLines.length != 0;
    }

    private async _removeProduct(productCode: string): Promise<CartLine | null> {
        // Check if an cartLine with the given productCode exists
        let cartLineDB = await this.cartLineDAO.find(productCode, "");
        if (!cartLineDB) {
            return null;
        }

        return this.cartLineDAO.remove(cartLineDB);
    }

    private async _removePromotion(promotionCode: string): Promise<CartLine[]> {
        // Remove all cartLines with the given promotionCode
        const promotionCartLines = await this.cartLineDAO.findByPromotionCode(promotionCode);
        return this.cartLineDAO.removeAll(promotionCartLines);
    }

}

